import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { LogUtils } from "abweb/utils/log/LogUtils";
import * as Draft from "draft-js";
const LOG = LogUtils.getLogger(__filename);
function isElement(node) {
  if (!node || !node.ownerDocument) {
    return false;
  }
  return node.nodeType === Node.ELEMENT_NODE;
}
function isText(node) {
  if (!node || !node.ownerDocument) {
    return false;
  }
  return node.nodeType === Node.TEXT_NODE;
}
function decodeOffsetKey(offsetKey) {
  const reverse = offsetKey.split("-").reverse();
  const leafKey = reverse[0];
  const decoratorKey = reverse[1];
  const blockKeyParts = reverse.slice(2);
  return {
    blockKey: blockKeyParts.reverse().join("-"),
    decoratorKey: parseInt(decoratorKey, 10),
    leafKey: parseInt(leafKey, 10)
  };
}
function debug(x) {
  if (1 + 1 === 0) {
    // unlikely
    LOG.debug(x);
  }
}
export function dumpSelectionState(selectionState) {
  debug(JSON.stringify(selectionState.toJS()));
}

/**
 * @deprecated Replace {@link AbMarkdownEditor} with {@link AbMarkdownLexical} markdown editor
 */
export class AbMarkdownEditorUtils {
  constructor(editor) {
    _defineProperty(this, "editor", void 0);
    _defineProperty(this, "selectionChanged", () => {
      const sel = document.getSelection();
      if (sel) {
        debug(`selectionChanged: ${JSON.stringify(this.editor.state.editorState.getSelection(), undefined, 2)}`);
        debug(`dom sel: ${sel}`);
        let anchor = sel.anchorNode;
        debug(`anchor: ${anchor} ${sel.anchorOffset}`);
        if (isElement(anchor)) {
          debug("ANCHOR IS ELEMENT");
          if (anchor.hasChildNodes()) {
            anchor = anchor.firstChild;
          }
        }
        if (isText(anchor) && AbMarkdownEditorUtils.getAttrForNode("data-entity-key", anchor)) {
          debug("ON ENTITY");
          const editorState = this.editor.state.editorState;
          const selection = editorState.getSelection();
          const newKey = selection.getAnchorKey();
          let {
            inside,
            start,
            end
          } = this.findEntity(editorState, selection.getAnchorKey(), selection.getAnchorOffset());
          const newOffset = this.editor.lastKey === "ArrowRight" || this.editor.lastKey === "End" ? end : start;
          window.newOffset = newOffset;
          window.newKey = selection.getAnchorKey();

          // If dom cursor is the START OF TEXT of the entity, move the dom cursor to BEFORE the person.  Don't touch the editor selection.
          if (sel.anchorOffset === 0) {
            let alphaNode = this.findTextBackwards("\u200b" /* "\u03b1" */, anchor);
            if (alphaNode) document.getSelection().setPosition(alphaNode, 0);
          } else {
            if (newOffset >= 0 && inside) {
              const newSelection = selection.merge({
                anchorKey: newKey,
                anchorOffset: newOffset,
                focusKey: newKey,
                focusOffset: newOffset
              });
              debug(`selectionWanted: ${JSON.stringify(newSelection, undefined, 2)}`);
            }
          }
        }
      }
    });
    _defineProperty(this, "oldOnSelect", void 0);
    this.editor = editor;
  }
  findTextBackwards(text, node) {
    if (node) {
      if (isText(node) && node.wholeText === text) {
        return node;
      }
      if (node.previousSibling) {
        return this.findTextBackwards(text, node.previousSibling);
      }
      if (isElement(node.parentNode) && node.parentNode.tagName === "SPAN") {
        return this.findTextBackwards(text, node.parentNode);
      }
    }
    return null;
  }
  findTextForwards(text, node, offset) {
    if (offset === undefined) offset = 0;
    if (node) {
      if (isText(node) && node.wholeText === text) {
        return node;
      }
      if (isElement(node)) {
        // If I have children, look in them left-to-right.
        const children = node.childNodes;
        for (let i = offset; i < children.length; i++) {
          const child = children.item(i);
          const found = this.findTextForwards(text, child);
          if (found) return found;
        }
      }
      // It's not me, and it's not my children.  Look in my next sibling.
      if (node.nextSibling) {
        return this.findTextForwards(text, node.nextSibling);
      }
      // I have no next sibling.  Look in my parent, but tell them to skip me.
      if (isElement(node.parentNode) && node.parentNode.tagName === "SPAN") {
        // If we are in the middle of a span, start looking at my parent,
        let whereAmI = -1;
        node.parentNode.childNodes.forEach((value, key) => {
          if (value === node) whereAmI = key;
        });
        if (whereAmI === -1) return null;
        return this.findTextForwards(text, node.parentNode, whereAmI + 1);
      }
    }
    return null;
  }
  nodeToSelectionState(node, before) {
    const editorState = this.editor.state.editorState;
    const contentState = editorState.getCurrentContent();
    const offsetKey = AbMarkdownEditorUtils.getAttrForNode("data-offset-key", node);
    if (!offsetKey) return null;
    const {
      blockKey,
      decoratorKey,
      leafKey
    } = decodeOffsetKey(offsetKey);
    const blockTree = editorState.getBlockTree(blockKey);
    let leaf = blockTree.getIn([decoratorKey + (before ? 0 : 1), "leaves", leafKey]);
    let leafStart = leaf.get("start");
    // if (before) {
    //   if (leafStart === 0) {
    //     // TODO: Previous block end?
    //     leaf = blockTree.getIn([decoratorKey - 1, "leaves", 0]);
    //     leafStart = leaf.get("end");
    //   }
    //   else {
    //     leafStart = leafStart - 1;
    //   }
    // }

    const selection = editorState.getSelection();
    return selection.merge({
      anchorKey: blockKey,
      anchorOffset: leafStart,
      focusKey: blockKey,
      focusOffset: leafStart
    });
  }
  findEntity(editorState, key, offset) {
    const content = editorState.getCurrentContent();
    const block = content.getBlockForKey(key);
    let inside = false;
    let before = false;
    let after = false;
    let start = -1;
    let end = -1;
    block.findEntityRanges(character => {
      const entityKey = character.getEntity();
      return entityKey !== null;
    }, (entity_start, entity_end) => {
      if (offset > entity_start && offset < entity_end) {
        inside = true;
        start = entity_start;
        end = entity_end;
      }
      if (offset === entity_end) {
        after = true;
        start = entity_start;
        end = entity_end;
      }
      if (offset === entity_start) {
        before = true;
        start = entity_start;
        end = entity_end;
      }
    });
    debug(`findEntity(${key},${offset}) => before:${before} in:${inside} after:${after} start:${start} end:${end}`);
    return {
      before,
      inside,
      after,
      start,
      end
    };
  }
  moveSelectionForward(editorState) {
    const selection = editorState.getSelection(); // Should eventually make this an invariant

    if (!selection.isCollapsed()) {
      return editorState; // not collapsed?  Do nothing
    }
    const key = selection.getAnchorKey();
    const offset = selection.getAnchorOffset();
    const content = editorState.getCurrentContent();
    let newKey = key;
    let newOffset;
    const block = content.getBlockForKey(key);
    if (block.getText().length - offset < 1) {
      newKey = content.getKeyAfter(key);
      newOffset = 0;
    } else {
      newOffset = offset + 1;
    }
    if (!newKey) {
      return editorState; // at the end of text? Do nothing
    }
    let {
      inside,
      end
    } = this.findEntity(editorState, newKey, newOffset);
    if (inside) {
      newOffset = end;
    }
    return Draft.EditorState.forceSelection(editorState, selection.merge({
      anchorKey: newKey,
      anchorOffset: newOffset,
      focusKey: newKey,
      focusOffset: newOffset
    }));
  }
  moveSelectionBackward(editorState) {
    const selection = editorState.getSelection(); // Should eventually make this an invariant

    if (!selection.isCollapsed()) {
      return editorState;
    } // not collapsed?  Do nothing

    const content = editorState.getCurrentContent();
    const key = selection.getAnchorKey();
    const offset = selection.getAnchorOffset();
    let newKey = key;
    let newOffset = offset;
    if (offset < 1) {
      // at the beginning of a block?
      newKey = content.getKeyBefore(key);
      if (newKey == null) {
        // no block before, so leave it alone
        newKey = key;
      } else {
        const blockBefore = content.getBlockForKey(newKey);
        newOffset = blockBefore.getText().length;
      }
    } else {
      newOffset = offset - 1;
    }
    let {
      inside,
      start
    } = this.findEntity(editorState, newKey, newOffset);
    if (inside) {
      newOffset = start;
    }
    return Draft.EditorState.forceSelection(editorState, selection.merge({
      anchorKey: newKey,
      anchorOffset: newOffset,
      focusKey: newKey,
      focusOffset: newOffset
    }));
  }
  moveSelectionToEndOfLine(editorState) {
    const selection = editorState.getSelection(); // Should eventually make this an invariant

    if (!selection.isCollapsed()) {
      return editorState; // not collapsed?  Do nothing
    }
    const key = selection.getAnchorKey();
    const content = editorState.getCurrentContent();
    const block = content.getBlockForKey(key);
    const newOffset = block.getText().length;
    return Draft.EditorState.forceSelection(editorState, selection.merge({
      anchorKey: key,
      anchorOffset: newOffset,
      focusKey: key,
      focusOffset: newOffset
    }));
  }
  newSelect(e) {
    this.oldOnSelect?.(e);
    debug(`cursor moved: ${JSON.stringify(this.editor.state.editorState.getSelection(), undefined, 2)}`);
  }
  static getAttrForNode(attr, node) {
    let searchNode = node;
    if (isElement(searchNode)) {
      const offsetKey = searchNode.getAttribute(attr);
      if (offsetKey) {
        return offsetKey;
      }
    }
    const parent = searchNode.parentElement;
    if (parent) return this.getAttrForNode(attr, parent);
    return undefined;
  }
}
//# sourceMappingURL=AbMarkdownEditorUtils.js.map
